import type { HumanMessageDisplayContext } from '@atlassian/conversation-assistant-service-api/types/assistance-service';

import { MAX_VISIBLE_SELECTED_WORK_ITEMS } from '../../common/constants/display-context';

const JIRA_ISSUE_KEY_PATTERN = /^[A-Z][A-Z0-9_]*-\d+$/u;

type NormalizedSelectedIssue = {
	identifier: string;
	issueKey?: string;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
	typeof value === 'object' && value !== null;

const normalizeIdentifier = (value: unknown): string | undefined =>
	typeof value === 'string' ? value.trim().toUpperCase() || undefined : undefined;

const getNormalizedSelectedIssues = (
	jiraViewContext: Record<string, unknown>,
): NormalizedSelectedIssue[] => {
	const metadataIssues = Array.isArray(jiraViewContext.selectedIssuesMetadata)
		? jiraViewContext.selectedIssuesMetadata.flatMap((value): NormalizedSelectedIssue[] => {
				if (!isRecord(value)) {
					return [];
				}
				const normalizedKey = normalizeIdentifier(value.key);
				const identifier = normalizedKey ?? normalizeIdentifier(value.id);
				if (!identifier) {
					return [];
				}
				return [
					{
						identifier,
						...(normalizedKey && JIRA_ISSUE_KEY_PATTERN.test(normalizedKey)
							? { issueKey: normalizedKey }
							: {}),
					},
				];
			})
		: [];
	const identifierIssues = Array.isArray(jiraViewContext.selectedIssueIdentifiers)
		? jiraViewContext.selectedIssueIdentifiers.flatMap((value): NormalizedSelectedIssue[] => {
				const identifier = normalizeIdentifier(value);
				if (!identifier) {
					return [];
				}
				return [
					{
						identifier,
						...(JIRA_ISSUE_KEY_PATTERN.test(identifier) ? { issueKey: identifier } : {}),
					},
				];
			})
		: [];

	const uniqueIssues = new Map<string, NormalizedSelectedIssue>();
	for (const issue of metadataIssues.length > 0 ? metadataIssues : identifierIssues) {
		if (!uniqueIssues.has(issue.identifier)) {
			uniqueIssues.set(issue.identifier, issue);
		}
	}

	return [...uniqueIssues.values()].sort((first, second) =>
		first.identifier < second.identifier ? -1 : first.identifier > second.identifier ? 1 : 0,
	);
};

const buildIssueUrl = (
	sourceUrl: string | undefined,
	issueKey: string | undefined,
): string | undefined => {
	if (!sourceUrl || !issueKey) {
		return undefined;
	}

	try {
		const issueUrl = new URL(sourceUrl);
		if (
			(issueUrl.protocol !== 'http:' && issueUrl.protocol !== 'https:') ||
			!issueUrl.hostname ||
			issueUrl.username ||
			issueUrl.password
		) {
			return undefined;
		}

		issueUrl.pathname = `/browse/${issueKey}`;
		issueUrl.search = '';
		issueUrl.hash = '';
		return issueUrl.toString();
	} catch {
		return undefined;
	}
};

export const buildOptimisticSelectedWorkDisplayContext = (
	aiFeatureContext: Record<string, unknown> | null | undefined,
): HumanMessageDisplayContext | undefined => {
	const jiraViewContext = aiFeatureContext?.jira_view_context;
	if (!isRecord(jiraViewContext)) {
		return undefined;
	}

	const selectedIssues = getNormalizedSelectedIssues(jiraViewContext);
	if (selectedIssues.length === 0) {
		return undefined;
	}
	const sourceUrl = typeof jiraViewContext.url === 'string' ? jiraViewContext.url : undefined;

	return {
		references: [
			{
				type: 'SELECTED_WORK',
				selected_work: {
					...(sourceUrl !== undefined ? { source_url: sourceUrl } : {}),
					total_count: selectedIssues.length,
					items: selectedIssues.slice(0, MAX_VISIBLE_SELECTED_WORK_ITEMS).map((issue) => {
						const issueUrl = buildIssueUrl(sourceUrl, issue.issueKey);
						return {
							identifier: issue.identifier,
							issue_key: issue.issueKey,
							...(issueUrl ? { issue_url: issueUrl } : {}),
						};
					}),
				},
			},
		],
	};
};
